import type { OrganizationResponseDto } from '@trellis/api'

export type CompletenessGroup = 'business' | 'tax' | 'branding' | 'payment'

export interface MissingProfileField {
  label: string
  settingsPath: string
}

export interface ProfileCompleteness {
  percent: number
  missingFields: MissingProfileField[]
}

const BUSINESS_SETTINGS_PATH = '/settings/organization'
const TAX_SETTINGS_PATH = '/settings/organization/tax'
const BRANDING_SETTINGS_PATH = '/settings/organization/branding'
const PAYMENT_SETTINGS_PATH = '/settings/organization/payment-options'

interface FieldCheck {
  label: string
  settingsPath: string
  group: CompletenessGroup
  isMissing: (organization: Partial<OrganizationResponseDto>) => boolean
}

const FIELD_CHECKS: FieldCheck[] = [
  {
    label: 'Business name',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.name,
  },
  {
    label: 'Email',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.email,
  },
  {
    label: 'Phone',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.phone,
  },
  {
    label: 'Address',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.address,
  },
  {
    label: 'Country',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.country,
  },
  {
    label: 'Timezone',
    settingsPath: BUSINESS_SETTINGS_PATH,
    group: 'business',
    isMissing: (organization) => !organization.timezone,
  },
  {
    label: 'Tax ID type',
    settingsPath: TAX_SETTINGS_PATH,
    group: 'tax',
    isMissing: (organization) => !organization.taxIdType,
  },
  {
    label: 'Tax ID',
    settingsPath: TAX_SETTINGS_PATH,
    group: 'tax',
    isMissing: (organization) => !organization.taxIdValue,
  },
  {
    label: 'Default tax type',
    settingsPath: TAX_SETTINGS_PATH,
    group: 'tax',
    isMissing: (organization) => !organization.defaultTaxType,
  },
  {
    label: 'Default tax rate',
    settingsPath: TAX_SETTINGS_PATH,
    group: 'tax',
    isMissing: (organization) => organization.defaultTaxRate === null,
  },
  {
    label: 'Registered for GST',
    settingsPath: TAX_SETTINGS_PATH,
    group: 'tax',
    isMissing: (organization) => organization.gstRegistered === null,
  },
  {
    label: 'Logo',
    settingsPath: BRANDING_SETTINGS_PATH,
    group: 'branding',
    isMissing: (organization) =>
      !organization.logoUrls?.sm && !organization.logoSmallUrls?.sm,
  },
  {
    label: 'Payment method',
    settingsPath: PAYMENT_SETTINGS_PATH,
    group: 'payment',
    isMissing: (organization) => !organization.paymentOptions?.length,
  },
]

/**
 * Accepts a partial organization so callers can check completeness against in-progress
 * form values (e.g. the onboarding wizard's Next button) without a full DTO.
 */
export function computeGroupCompleteness(
  organization: Partial<OrganizationResponseDto> | null,
  group: CompletenessGroup,
): ProfileCompleteness {
  const groupChecks = FIELD_CHECKS.filter((check) => check.group === group)

  if (!organization) {
    return {
      percent: 0,
      missingFields: groupChecks.map(({ label, settingsPath }) => ({
        label,
        settingsPath,
      })),
    }
  }

  const missingFields = groupChecks
    .filter((check) => check.isMissing(organization))
    .map(({ label, settingsPath }) => ({ label, settingsPath }))

  const percent = Math.round(
    ((groupChecks.length - missingFields.length) / groupChecks.length) * 100,
  )

  return { percent, missingFields }
}

export interface OverallCompleteness {
  business: boolean
  businessPercent: number
  tax: boolean
  taxPercent: number
  branding: boolean
  brandingPercent: number
  payment: boolean
  paymentPercent: number
  isComplete: boolean
}

export function computeOverallCompleteness(
  organization: OrganizationResponseDto | null,
): OverallCompleteness {
  const businessPercent = computeGroupCompleteness(organization, 'business').percent
  const taxPercent = computeGroupCompleteness(organization, 'tax').percent
  const brandingPercent = computeGroupCompleteness(organization, 'branding').percent
  const paymentPercent = computeGroupCompleteness(organization, 'payment').percent

  const business = businessPercent === 100
  const tax = taxPercent === 100
  const branding = brandingPercent === 100
  const payment = paymentPercent === 100

  return {
    business,
    businessPercent,
    tax,
    taxPercent,
    branding,
    brandingPercent,
    payment,
    paymentPercent,
    // Branding (logo) is a nice-to-have, not a requirement — onboarding is
    // considered complete once business, tax, and payment are all done.
    isComplete: business && tax && payment,
  }
}
